import { useEffect, useRef } from "react";

import { animate, duration, easing } from "@/shared/lib";
import { tactile } from "@/shared/telegram";

/**
 * Многострочное поле с счётчиком символов.
 *
 * Собран по образцу `AmountInput`, и не случайно: это второе поле ввода в
 * приложении, и оно обязано вести себя так же — та же рамка, та же встряска
 * при отказе сервера, та же тактильная отдача. Разойдясь, два поля выглядели
 * бы как две разные системы на соседних экранах.
 *
 * Счётчик показывается не всегда: пока до предела далеко, он лишний шум. Он
 * появляется на последней четверти — там, где начинает влиять на решение
 * «дописывать или сокращать».
 */
interface TextAreaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  maxLength: number;
  hint?: string;
  /** Текст ошибки от бэкенда — показывается вместо подсказки, у поля. */
  error?: string | undefined;
  rows?: number;
}

export function TextArea({
  value,
  onChange,
  placeholder,
  label,
  maxLength,
  hint,
  error,
  rows = 4,
}: TextAreaProps) {
  const ref = useRef<HTMLLabelElement>(null);

  useEffect(() => {
    if (!error) return;
    const element = ref.current;
    if (!element) return;

    animate(
      element,
      [
        { transform: "translate3d(0, 0, 0)" },
        { transform: "translate3d(-6px, 0, 0)" },
        { transform: "translate3d(5px, 0, 0)" },
        { transform: "translate3d(-3px, 0, 0)" },
        { transform: "translate3d(0, 0, 0)" },
      ],
      { duration: duration("slow"), easing: easing("standard") },
    );
    tactile("error");
  }, [error]);

  const showCounter = value.length > maxLength * 0.75;

  return (
    <div className="flex flex-col gap-2">
      <label
        ref={ref}
        className={[
          "flex flex-col rounded-2xl border bg-background-elevated px-4 py-3",
          "transition-colors duration-[var(--motion-fast)] ease-standard",
          error ? "border-border-loss" : "border-border-subtle",
        ].join(" ")}
      >
        <span className="sr-only">{label}</span>
        <textarea
          value={value}
          rows={rows}
          // Обрезаем на вводе, а не на отправке: 422 после набранных двух тысяч
          // символов — худший момент, чтобы узнать про предел.
          onChange={(event) => {
            onChange(event.target.value.slice(0, maxLength));
          }}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          className="w-full resize-none bg-transparent text-body font-normal text-text-primary outline-none placeholder:text-text-tertiary"
        />
        {showCounter ? (
          <span className="self-end pt-1 text-micro font-medium tabular-nums text-text-tertiary">
            {value.length} / {maxLength}
          </span>
        ) : null}
      </label>

      {error ? (
        <span role="alert" className="animate-rise-in text-caption font-medium text-status-loss">
          {error}
        </span>
      ) : hint ? (
        <span className="text-caption font-normal text-text-secondary">{hint}</span>
      ) : null}
    </div>
  );
}
